/**
 * How an annotated screenshot travels toward CoPaya.
 *
 * CoPaya's portal-entry contract has no attachment channel (only the two
 * prefill claims), so v1 hands the image over via the user's clipboard and
 * the user pastes it into the CoPaya form. The seam exists so a future
 * variant (upload-to-S3 + link in the summary, or an entry-token claim once
 * the DevPaya contract grows one) can replace the clipboard without touching
 * the editor or the orchestrator beyond the toast per method.
 */

export type DeliveryResult =
  | { method: 'clipboard' }
  | { method: 'download'; filename: string };

export interface ScreenshotDelivery {
  /**
   * MUST be invoked synchronously inside a user gesture (the Send click):
   * the clipboard write is gesture-gated, and Safari additionally requires
   * the ClipboardItem to be constructed with the still-pending blob promise
   * rather than an awaited blob.
   */
  deliver(pngBlob: Promise<Blob>): Promise<DeliveryResult>;
}

const DOWNLOAD_FILENAME = 'copaya-screenshot.png';

async function downloadFallback(pngBlob: Promise<Blob>): Promise<DeliveryResult> {
  const blob = await pngBlob;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = DOWNLOAD_FILENAME;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
  return { method: 'download', filename: DOWNLOAD_FILENAME };
}

export const clipboardDelivery: ScreenshotDelivery = {
  async deliver(pngBlob: Promise<Blob>): Promise<DeliveryResult> {
    if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      return downloadFallback(pngBlob);
    }
    try {
      // Constructed and written synchronously with the promise (the try
      // block runs synchronously up to the await) — see the interface
      // contract. Construction itself can throw synchronously where promised
      // blob data is unsupported, which must also fall back, not bubble.
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
      return { method: 'clipboard' };
    } catch {
      // Unsupported promised data, permission denied, or focus already
      // moved to the CoPaya tab.
      return downloadFallback(pngBlob);
    }
  },
};
